import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t } from "../shared/utils"

const STORAGE_KEY = "partner_search_view"

export default class extends Controller {
  static targets = ["results", "sentinel", "form", "count", "viewTiles", "viewList"]
  static values = { apiUrl: String }

  connect() {
    loadTranslations()
    this.page = 1
    this.hasMore = false
    this.loading = false
    this.filterTimeout = null
    this.view = (typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "list"
    this._applyView()
    this._setupInfiniteScroll()
    this._setupFilters()
    this._fetchResults(1, true)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.hasFormTarget && this._boundFilter) {
      this.formTarget.removeEventListener("input", this._boundFilter)
      this.formTarget.removeEventListener("change", this._boundFilter)
      this.formTarget.removeEventListener("submit", this._boundSubmit)
    }
  }

  setViewTiles() {
    this.view = "tiles"
    this._applyView()
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "tiles")
  }

  setViewList() {
    this.view = "list"
    this._applyView()
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "list")
  }

  _applyView() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.classList.remove("partner-results--tiles", "partner-results--list")
    this.resultsTarget.classList.add(`partner-results--${this.view}`)
    if (this.hasViewTilesTarget) this.viewTilesTarget.classList.toggle("active", this.view === "tiles")
    if (this.hasViewListTarget) this.viewListTarget.classList.toggle("active", this.view === "list")
  }

  _setupFilters() {
    if (!this.hasFormTarget) return
    this._boundFilter = this._onFilterChange.bind(this)
    this._boundSubmit = this._onSubmit.bind(this)
    this.formTarget.addEventListener("input", this._boundFilter)
    this.formTarget.addEventListener("change", this._boundFilter)
    this.formTarget.addEventListener("submit", this._boundSubmit)
  }

  _onFilterChange(event) {
    if (event.target.name === "search_mode") return
    const isCityInput = event.target.name === "city" && event.type === "input"
    if (isCityInput) {
      const val = (event.target.value || "").trim()
      if (val.length >= 3 || val.length === 0) {
        clearTimeout(this.filterTimeout)
        this.filterTimeout = setTimeout(() => this._fetchResults(1, true), 400)
      }
      return
    }
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this._fetchResults(1, true), 200)
  }

  _onSubmit(event) {
    event.preventDefault()
    this._fetchResults(1, true)
  }

  _setupInfiniteScroll() {
    if (!this.hasSentinelTarget) return
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && this.hasMore && !this.loading) {
        this._fetchResults(this.page + 1, false)
      }
    }, { rootMargin: "200px", threshold: 0.1 })
    this.observer.observe(this.sentinelTarget)
  }

  async _fetchResults(page, replace) {
    if (this.loading) return
    this.loading = true

    if (replace) {
      this.resultsTarget.style.opacity = "0.4"
      this.resultsTarget.style.pointerEvents = "none"
    } else if (this.hasSentinelTarget) {
      this.sentinelTarget.innerHTML = '<div class="text-center py-3"><i class="fa-solid fa-spinner fa-spin"></i></div>'
    }

    try {
      const params = this._buildParams(page)
      const json = await apiGet(this.apiUrlValue, params)
      const { announcements, dancers } = json.data
      const { total_count, has_more, current_page } = json.meta

      if (replace) {
        const items = [...announcements.map(a => this._renderAnnouncementRow(a)), ...dancers.map(d => this._renderDancerRow(d))]
        this.resultsTarget.innerHTML = items.length > 0 ? items.join("") : this._renderEmpty()
      } else {
        dancers.forEach(d => this.resultsTarget.insertAdjacentHTML("beforeend", this._renderDancerRow(d)))
      }

      this.page = current_page
      this.hasMore = has_more
      if (this.hasCountTarget) this.countTarget.textContent = String(total_count)

      if (this.hasSentinelTarget) {
        this.sentinelTarget.innerHTML = this.hasMore
          ? '<div class="text-center py-3"><i class="fa-solid fa-spinner fa-spin"></i></div>'
          : ""
      }
    } catch (error) {
      console.error("Error fetching partners:", error)
      if (this.hasSentinelTarget) this.sentinelTarget.innerHTML = ""
    } finally {
      this.loading = false
      this.resultsTarget.style.opacity = ""
      this.resultsTarget.style.pointerEvents = ""
    }
  }

  _buildParams(page) {
    if (!this.hasFormTarget) return { page }
    const formData = new FormData(this.formTarget)
    const params = {}
    for (const [key, value] of formData.entries()) {
      if (value) {
        if (key.endsWith("[]")) {
          const k = key.slice(0, -2)
          if (!params[k]) params[k] = []
          params[k].push(value)
        } else {
          params[key] = value
        }
      }
    }
    params.page = page
    return params
  }

  _renderAnnouncementRow(a) {
    const avatarHtml = this._avatarHtml(a.avatar_url, a.user_sex, 48)
    const location = this._locationParts(a.city, a.voivodeship)
    const ageInfo = a.age_categories?.length > 0
      ? a.age_categories.join(", ")
      : (a.user_age ? `${a.user_age} ${t("dancer_profile.years_old")}` : (a.federation_age_category || ""))
    const classesHtml = this._classesHtml(a.dance_classes)
    const stylesHtml = (a.dance_styles || []).map(s => `<span class="badge bg-light text-dark border small">${escapeHtml(s)}</span>`).join("")
    const titleShort = (a.title || "").split(" ").slice(0, 7).join(" ") + ((a.title || "").split(" ").length > 7 ? "..." : "")

    return `<div class="partner-result-item">
      <a href="${escapeHtml(a.url)}" class="text-decoration-none text-reset d-block">
        <div class="d-flex align-items-center gap-3 p-3 bg-white rounded shadow-sm mb-2 partner-row">
          <div class="flex-shrink-0">${avatarHtml}</div>
          <div class="flex-grow-1 min-width-0">
            <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
              <span class="fw-semibold">${escapeHtml(a.user_name)}</span>
              <span class="text-truncate text-muted">${escapeHtml(titleShort)}</span>
              <span class="badge bg-info bg-opacity-25 text-dark small">${escapeHtml(t("has_announcement"))}</span>
            </div>
            <div class="d-flex align-items-center gap-3 text-muted small mb-1">
              ${location ? `<span><i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(location)}</span>` : ""}
              ${ageInfo ? `<span><i class="fa-solid fa-user me-1"></i>${escapeHtml(ageInfo)}</span>` : ""}
            </div>
            <div class="d-flex align-items-center gap-2 flex-wrap">${classesHtml}${stylesHtml}</div>
          </div>
          <div class="flex-shrink-0 text-muted"><i class="fa-solid fa-chevron-right"></i></div>
        </div>
      </a>
    </div>`
  }

  _renderDancerRow(d) {
    const avatarHtml = this._avatarHtml(d.avatar_url, d.sex, 48)
    const ageInfo = d.age ? `${d.age} ${t("dancer_profile.years_old")}` : (d.federation_age_category || "")
    const classesHtml = this._classesHtml(d.dance_classes)
    const stylesHtml = (d.dance_styles || []).map(s => `<span class="badge bg-light text-dark border small">${escapeHtml(s)}</span>`).join("")

    let membershipHtml = ""
    if (d.memberships) {
      d.memberships.forEach(m => {
        if (m.has_subscription) {
          membershipHtml += `<span class="badge bg-success small" title="${escapeHtml(m.club_name)}">${escapeHtml(m.club_name)}</span>`
        }
        m.federations.forEach(f => {
          membershipHtml += `<span class="badge bg-secondary small">${escapeHtml(f)}</span>`
        })
      })
    }

    return `<div class="partner-result-item">
      <a href="${escapeHtml(d.url)}" class="text-decoration-none text-reset d-block">
        <div class="d-flex align-items-center gap-3 p-3 bg-white rounded shadow-sm mb-2 partner-row dancer-card">
          <div class="flex-shrink-0">${avatarHtml}</div>
          <div class="flex-grow-1 min-width-0">
            <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
              <span class="fw-semibold">${escapeHtml(d.display_name)}</span>
              ${membershipHtml}
            </div>
            <div class="d-flex align-items-center gap-3 text-muted small mb-1">
              ${d.city ? `<span><i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(d.city)}</span>` : ""}
              ${ageInfo ? `<span><i class="fa-solid fa-user me-1"></i>${escapeHtml(ageInfo)}</span>` : ""}
            </div>
            <div class="d-flex align-items-center gap-2 flex-wrap">${classesHtml}${stylesHtml}</div>
          </div>
          <div class="flex-shrink-0 text-muted"><i class="fa-solid fa-chevron-right"></i></div>
        </div>
      </a>
    </div>`
  }

  _avatarHtml(url, sex, size) {
    if (url) {
      return `<img src="${escapeHtml(url)}" class="rounded-circle" style="width: ${size}px; height: ${size}px; object-fit: cover;" alt="">`
    }
    const isMale = sex === 1 || sex === "male"
    const bg = isMale ? "#e3f2fd" : "#fce4ec"
    const icon = isMale ? "fa-mars text-primary" : "fa-venus text-danger"
    return `<div class="d-inline-flex align-items-center justify-content-center rounded-circle" style="width: ${size}px; height: ${size}px; background: ${bg};"><i class="fa-solid ${icon}"></i></div>`
  }

  _classesHtml(classes) {
    if (!classes) return ""
    let html = ""
    if (classes.st) html += `<span class="badge bg-dm-navy small">ST: ${escapeHtml(classes.st)}</span>`
    if (classes.la) html += `<span class="badge bg-dm-navy small">LA: ${escapeHtml(classes.la)}</span>`
    return html
  }

  _locationParts(city, voivodeship) {
    const parts = []
    if (city) parts.push(city)
    if (voivodeship) {
      const label = t(`voivodeships.${voivodeship}`)
      parts.push(label !== `voivodeships.${voivodeship}` ? label : voivodeship)
    }
    return parts.join(", ")
  }

  _renderEmpty() {
    return `<div class="partners-search__empty">
      <div class="text-center py-5">
        <i class="fa-solid fa-search fa-3x text-muted mb-3"></i>
        <h4>${escapeHtml(t("no_results"))}</h4>
        <p class="text-muted">${escapeHtml(t("try_different_filters"))}</p>
      </div>
    </div>`
  }
}
